import { SubscriptionActionButton } from "@/components/subscription-action-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useSubscribeEligibility } from "@/hooks/useSubscribeEligibility";
import {
  LIBUM_DEV_MAIL_URL,
  LIBUM_POWERON_PRO_OFFERINGS,
} from "@/lib/constants";
import {
  getSubscriptionMetadata,
  SubscriptionCollectionMethod,
  SubscriptionProps,
} from "@/types/subscription";
import { ArrowRight, Infinity, LoaderCircle } from "lucide-react";
import { SmoothHeight, useSawLoading } from "@/components/loading-skeletons";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function SubscriptionPowerOnPro({
  isAdmin,
  isMatchLoading,
  isSubscribeLoading = false,
  isQuantityLoading = false,
  isBillingBusy = false,
  user,
  organization,
  subscription,
  paymentMethod,
  members,
  taxExemption,
  onSubscribe,
  onMatch,
  onQuantityChange,
}: SubscriptionProps) {
  const { isSubscribeDisabled, isPendingTaxExemption } =
    useSubscribeEligibility(organization, taxExemption);

  const sawMatchLoading = useSawLoading(isMatchLoading);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [seatsUsedPlus, setSeatsUsedPlus] = useState(0);

  const shouldShowSeats =
    isAdmin &&
    subscription?.paymentMethod !== SubscriptionCollectionMethod.INVOICE;

  const getPlusTotalCost = (): string => {
    const metadata = getSubscriptionMetadata(paymentMethod);
    const formattedAmount = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format((subscription?.seats || 0) * metadata.powerOnProPrice);

    return formattedAmount.slice(0, -3);
  };

  const getPlusProgress = (): number => {
    const totalSeats = subscription?.seats || 0;
    const usedSeats = seatsUsedPlus;

    return totalSeats !== 0 ? (usedSeats / totalSeats) * 100 : 0;
  };

  useEffect(() => {
    setSeatsUsed(members.filter((m) => m.status === "active").length || 0);
    setSeatsUsedPlus(members.filter((m) => m.isPowerOnProPlus).length || 0);
  }, [members, user]);

  return (
    <>
      <div className="grid gap-3.5 sm:gap-5 md:gap-4 lg:grid-cols-2">
        {/* Free plan */}
        <Card>
          <CardHeader className="p-4 sm:p-8">
            <CardTitle className="mb-2 w-full text-base sm:text-lg">
              Free
            </CardTitle>
            <CardDescription className="text-sm">
              For developers who are just looking for a language server for
              PowerOn with limited connectivity features to Symitar
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-[180px] p-4 pt-0 sm:min-h-[202px] sm:p-8 sm:pt-0">
            <div className="mb-6 sm:mb-8">
              <span className="text-2xl font-bold text-gray-1100 dark:text-gray-100 sm:text-4xl">
                $0
              </span>
              <span className="ml-1 text-base text-muted sm:ml-1.5 sm:text-xl">
                per seat /{" "}
                {paymentMethod === "monthly"
                  ? "month"
                  : paymentMethod === "yearly"
                    ? "year"
                    : "3-years"}
              </span>
            </div>
            <Separator />
            <div className="mt-4 flex min-h-[80px] flex-row gap-2">
              <div className="flex w-full flex-col pb-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-1">
                  <h2 className="text-base font-medium text-gray-1100 dark:text-gray-100 sm:text-lg">
                    Seats
                  </h2>
                  <span className="flex flex-wrap items-center text-xs font-medium text-muted sm:text-sm">
                    <span className="mr-1 font-bold text-gray-1100 dark:text-gray-100">
                      {seatsUsed}
                    </span>
                    of
                    <span className="mx-1 flex items-center font-bold text-emerald-400">
                      <Infinity className="mr-0.5 size-4 sm:mr-1 sm:size-5" />
                      <span className="hidden sm:inline">seats</span>
                    </span>
                    used
                  </span>
                </div>
                <Progress value={100} className="mt-2.5" />
              </div>
              <div className="flex shrink-0 items-center">
                <span className="text-sm font-bold text-gray-1100 dark:text-gray-100 sm:ml-2 sm:text-base">
                  $0
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter
            className="cursor-pointer justify-center rounded-b-md border-t px-4 py-4 text-sm font-medium transition-colors duration-200 hover:bg-sidebar-accent hover:text-gray-1100 hover:dark:text-gray-100 sm:px-6 sm:py-6 sm:text-lg"
            onClick={() => {
              window.open(
                LIBUM_POWERON_PRO_OFFERINGS,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <span className="sm:hidden">Compare features</span>
            <span className="hidden sm:inline">View & compare features</span>
            <ArrowRight className="ml-1 mt-0.5 size-4 text-muted" />
          </CardFooter>
        </Card>
        {/* Plus plan */}
        <Card>
          <CardHeader className="p-4 sm:p-8">
            <CardTitle className="mb-2 text-base sm:text-lg">Plus</CardTitle>
            <CardDescription className="text-sm">
              Be the most equipped PowerOn developer with access to all
              connectivity features to Symitar and an{" "}
              <span className="font-bold text-emerald-400">advanced</span>{" "}
              toolkit
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-[180px] p-4 pt-0 sm:min-h-[202px] sm:p-8 sm:pt-0">
            <div className="mb-6 sm:mb-8">
              <span className="text-2xl font-bold text-gray-1100 dark:text-gray-100 sm:text-4xl">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                })
                  .format(
                    getSubscriptionMetadata(paymentMethod).powerOnProPrice,
                  )
                  .slice(0, -3)}
              </span>
              <span className="ml-1 text-base text-muted sm:ml-1.5 sm:text-xl">
                per seat /{" "}
                {paymentMethod === "monthly"
                  ? "month"
                  : paymentMethod === "yearly"
                    ? "year"
                    : "3-years"}
              </span>
            </div>
            <Separator />
            {subscription && subscription.seats > 0 ? (
              <div className="mt-4 flex flex-row gap-2">
                <div className="flex w-full flex-col pb-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-1">
                    <h2 className="text-base font-medium text-gray-1100 dark:text-gray-100 sm:text-lg">
                      Seats
                    </h2>
                    <span className="text-xs font-medium text-muted sm:text-sm">
                      <span className="font-bold text-gray-1100 dark:text-gray-100">
                        {seatsUsedPlus}
                      </span>{" "}
                      of{" "}
                      <span className="font-bold text-gray-1100 dark:text-gray-100">
                        {(subscription?.seats || 0) +
                          ` seat${subscription?.seats !== 1 ? "s" : ""}`}
                      </span>{" "}
                      used
                    </span>
                  </div>
                  <Progress value={getPlusProgress()} className="mt-2.5" />
                  {shouldShowSeats ? (
                    <SubscriptionActionButton
                      loading={isQuantityLoading}
                      disabled={isBillingBusy}
                      variant="link"
                      wrapperClassName="mt-2 justify-start"
                      className="h-auto p-0 text-xs font-medium text-brand-accent sm:text-sm"
                      onClick={() => onQuantityChange("poweron-pro")}
                    >
                      Add seats
                    </SubscriptionActionButton>
                  ) : (
                    <div className="mt-2 text-xs sm:text-sm">
                      To add seats, reach out to{" "}
                      <a
                        href={LIBUM_DEV_MAIL_URL}
                        className="cursor-pointer text-brand-accent hover:underline"
                      >
                        development@libum.io
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center">
                  <span className="text-sm font-bold text-gray-1100 dark:text-gray-100 sm:ml-2 sm:text-base">
                    {getPlusTotalCost()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-5 flex flex-col sm:mt-7">
                <SubscriptionActionButton
                  loading={isSubscribeLoading}
                  wrapperClassName="w-full"
                  className="w-full"
                  size="lg"
                  onClick={() => onSubscribe()}
                  disabled={isSubscribeDisabled || !isAdmin || isBillingBusy}
                >
                  Subscribe now
                </SubscriptionActionButton>
                <SmoothHeight className="flex flex-col">
                  {isMatchLoading ? (
                    <span
                      role="status"
                      className="mt-1 flex items-center gap-1.5 text-xs text-muted"
                    >
                      <LoaderCircle
                        aria-hidden="true"
                        className="size-3.5 animate-spin text-brand-primary"
                      />
                      Matching subscription…
                    </span>
                  ) : isPendingTaxExemption ? (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      Pending review of sales tax exemption. See{" "}
                      <Link
                        className="text-brand-accent"
                        href="/settings/sales-tax"
                      >
                        Sales Tax
                      </Link>{" "}
                      page for status.
                    </span>
                  ) : isAdmin ? (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      Already have a subscription?
                      <span
                        className="ml-1 cursor-pointer text-brand-accent hover:underline"
                        onClick={onMatch}
                      >
                        Click here
                      </span>{" "}
                      to try and match by email.
                    </span>
                  ) : (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      You must be the organization owner or an admin to
                      subscribe.
                    </span>
                  )}
                </SmoothHeight>
              </div>
            )}
          </CardContent>
          <CardFooter
            className="cursor-pointer justify-center rounded-b-md border-t px-4 py-4 text-sm font-medium transition-colors duration-200 hover:bg-sidebar-accent hover:text-gray-1100 hover:dark:text-gray-100 sm:px-6 sm:py-6 sm:text-lg"
            onClick={() => {
              window.open(
                LIBUM_POWERON_PRO_OFFERINGS,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <span className="sm:hidden">Compare features</span>
            <span className="hidden sm:inline">View & compare features</span>
            <ArrowRight className="ml-1 mt-0.5 size-4 text-muted" />
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
